import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Badge, Modal } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import { useTranslation } from "react-i18next";
import { SideBar } from "./sideBar";
import { NavBar } from "./navBar";
import { DataTablePagination } from "./utils/dataTablePagination";
import { DropDownClients } from "./clients/dropDownClients";
import ClipLoader from "react-spinners/ClipLoader";
import { useAuth } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import {
  getAllTickets,
  getTicketsByClient,
  getTicket,
  updateTicket,
  deleteTicket,
} from "../api/apiClient";
import {
  getApiResponseMessage,
  getApiErrorMessage,
} from "../utils/apiMessageHelper";
import formatDate from "../utils/formatDate";
import TicketDetailModalAdmin from "./ticketDetailModalAdmin";

export const MainTickets = () => {
  const { t } = useTranslation();
  const { clientId } = useAuth();
  const { notify } = useNotification();

  // Check if user is Super Admin (clientId === 1)
  const isSuperAdmin = clientId === 1;

  // States
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(
    isSuperAdmin ? null : clientId,
  );
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Permission check
  const hasPermissions = clientId !== null && clientId !== undefined;

  // Status, Priority, Category mappings
  const statusMap = {
    0: { label: t("tickets.statusNew"), variant: "primary" },
    1: { label: t("tickets.statusInProgress"), variant: "warning" },
    2: { label: t("tickets.statusResolved"), variant: "success" },
    3: { label: t("tickets.statusClosed"), variant: "secondary" },
  };

  const priorityMap = {
    0: { label: t("tickets.priorityLow"), variant: "info" },
    1: { label: t("tickets.priorityMedium"), variant: "secondary" },
    2: { label: t("tickets.priorityHigh"), variant: "warning" },
    3: { label: t("tickets.priorityUrgent"), variant: "danger" },
  };

  const categoryMap = {
    0: t("tickets.categoryErrors"),
    1: t("tickets.categoryInquiries"),
    2: t("tickets.categorySupportRequest"),
    3: t("tickets.categoryOther"),
  };

  // Table columns
  const ticketColumns = [
    {
      Header: " ",
      columns: [
        { Header: "ID", accessor: "id", width: 60 },
        { Header: t("tickets.tableHeaderTitle"), accessor: "title" },
        {
          Header: t("tickets.tableHeaderCategory"),
          accessor: "category",
          Cell: ({ value }) => (
            <Badge bg="light" text="dark">
              {categoryMap[value] || t("tickets.categoryOther")}
            </Badge>
          ),
        },
        {
          Header: t("tickets.tableHeaderStatus"),
          accessor: "status",
          Cell: ({ value }) => {
            const status = statusMap[value] || statusMap[0];
            return <Badge bg={status.variant}>{status.label}</Badge>;
          },
        },
        {
          Header: t("tickets.tableHeaderPriority"),
          accessor: "priority",
          Cell: ({ value }) => {
            const priority = priorityMap[value] || priorityMap[1];
            return <Badge bg={priority.variant}>{priority.label}</Badge>;
          },
        },
        {
          Header: t("tickets.tableHeaderCreatedAt"),
          accessor: "createdAt",
          Cell: ({ value }) => formatDate(value),
        },
        {
          Header: t("tickets.tableHeaderActions"),
          accessor: "actions",
          Cell: ({ row }) => (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleShowDetail(row.original)}
            >
              {t("common.edit")}
            </Button>
          ),
        },
      ],
    },
  ];

  // Load tickets on mount or when selectedClientId changes
  useEffect(() => {
    loadTickets();
  }, [selectedClientId]);

  // Filter tickets when filters change
  useEffect(() => {
    applyFilters();
  }, [tickets, searchTerm, filterStatus, filterPriority, filterCategory]);

  // Load tickets from API
  const loadTickets = async () => {
    setIsLoading(true);
    try {
      let response;
      if (isSuperAdmin && selectedClientId) {
        response = await getTicketsByClient(selectedClientId);
      } else if (isSuperAdmin && !selectedClientId) {
        response = await getAllTickets();
      } else {
        response = await getTicketsByClient(clientId);
      }

      if (response.success) {
        setTickets(response.data || []);
      } else {
        setTickets([]);
      }
    } catch (error) {
      console.error("Error loading tickets:", error);
      notify(t("tickets.loadError"), { type: "error" });
      setTickets([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters to tickets
  const applyFilters = () => {
    let filtered = [...tickets];

    // Search by title or description
    if (searchTerm) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Filter by status
    if (filterStatus !== "") {
      filtered = filtered.filter(
        (ticket) => ticket.status === parseInt(filterStatus),
      );
    }

    // Filter by priority
    if (filterPriority !== "") {
      filtered = filtered.filter(
        (ticket) => ticket.priority === parseInt(filterPriority),
      );
    }

    // Filter by category
    if (filterCategory !== "") {
      filtered = filtered.filter(
        (ticket) => ticket.category === parseInt(filterCategory),
      );
    }

    setFilteredTickets(filtered);
  };

  // Handle client change (Super Admin only)
  const handleClientChange = (selected) => {
    setSelectedClientId(selected?.idClient || null);
  };

  // Show detail modal
  const handleShowDetail = async (ticket) => {
    setCurrentTicket(ticket);

    // Load full ticket details including attachments
    const response = await getTicket(ticket.id);
    if (response.success) {
      setCurrentTicket(response.data);
    }

    setShowDetailModal(true);
  };

  // Close detail modal
  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setCurrentTicket(null);
  };

  // Delete ticket
  const handleDeleteTicket = async () => {
    if (!currentTicket) return;

    setIsUpdating(true);
    try {
      const response = await deleteTicket(currentTicket.id);

      if (response.success) {
        notify(
          getApiResponseMessage(t, response.data?.messageCode) ||
            t("tickets.deleteSuccess"),
          { type: "success" },
        );
        handleCloseDeleteConfirm();
        handleCloseDetail();
        loadTickets();
      } else {
        notify(getApiErrorMessage(t, response) || t("tickets.deleteError"), {
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting ticket:", error);
      notify(t("tickets.deleteError"), { type: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  // Show/hide delete confirmation
  const handleShowDeleteConfirm = () => setShowDeleteConfirm(true);
  const handleCloseDeleteConfirm = () => setShowDeleteConfirm(false);

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <Card>
              <Card.Header>
                <Card.Title as="h5">{t("tickets.pageTitle")}</Card.Title>
              </Card.Header>
              <Card.Body>
                {/* Filters */}
                <Row className="mb-3">
                  {/* Client dropdown (Super Admin only) */}
                  {isSuperAdmin && (
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>{t("tickets.filterByClient")}</Form.Label>
                        <DropDownClients
                          value={selectedClientId}
                          onChange={handleClientChange}
                          allowEmpty={true}
                        />
                      </Form.Group>
                    </Col>
                  )}

                  {/* Status filter */}
                  <Col md={isSuperAdmin ? 3 : 3}>
                    <Form.Group>
                      <Form.Label>{t("tickets.filterByStatus")}</Form.Label>
                      <Form.Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="">{t("tickets.allStatuses")}</option>
                        <option value="0">{statusMap[0].label}</option>
                        <option value="1">{statusMap[1].label}</option>
                        <option value="2">{statusMap[2].label}</option>
                        <option value="3">{statusMap[3].label}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Priority filter */}
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t("tickets.filterByPriority")}</Form.Label>
                      <Form.Select
                        value={filterPriority}
                        onChange={(e) => setFilterPriority(e.target.value)}
                      >
                        <option value="">{t("tickets.allPriorities")}</option>
                        <option value="0">{priorityMap[0].label}</option>
                        <option value="1">{priorityMap[1].label}</option>
                        <option value="2">{priorityMap[2].label}</option>
                        <option value="3">{priorityMap[3].label}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Category filter */}
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t("tickets.filterByCategory")}</Form.Label>
                      <Form.Select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                      >
                        <option value="">{t("tickets.allCategories")}</option>
                        <option value="0">{categoryMap[0]}</option>
                        <option value="1">{categoryMap[1]}</option>
                        <option value="2">{categoryMap[2]}</option>
                        <option value="3">{categoryMap[3]}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Search bar */}
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Control
                        type="text"
                        placeholder={t("tickets.searchPlaceholder")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Loading indicator */}
                {isLoading && (
                  <div className="text-center my-4">
                    <ClipLoader color="#0d6efd" size={50} />
                  </div>
                )}

                {/* Tickets table */}
                {!isLoading && filteredTickets.length > 0 && (
                  <DataTablePagination
                    columns={ticketColumns}
                    data={filteredTickets}
                  />
                )}

                {/* No data message */}
                {!isLoading && filteredTickets.length === 0 && (
                  <div className="text-center my-4">
                    <p>{t("tickets.noTicketsFound")}</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Row>
        </Container>
      </Container>

      {/* Detail Modal - Admin version with conversation functionality */}
      {currentTicket && (
        <TicketDetailModalAdmin
          show={showDetailModal}
          onHide={handleCloseDetail}
          ticket={currentTicket}
          onTicketUpdate={loadTickets}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={handleCloseDeleteConfirm}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("common.confirmDeleteTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("tickets.deleteConfirmMessage")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteConfirm}>
            {t("common.no")}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteTicket}
            disabled={isUpdating}
          >
            {isUpdating ? t("common.processing") : t("common.yes")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};
